import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BLOCK_TYPE_LABELS,
  BLOCK_TYPE_ORDER,
  type ComposerBlock,
  type ComposerBlockImageAlign,
  type ComposerBlockImagePosition,
  type ComposerBlockImageSize,
  type ComposerBlockType,
} from "@/lib/composerBlocks";
import BlockImageField from "./BlockImageField";
import { BlockBodyEditor } from "./BlockBodyEditor";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: ComposerBlock | null;
  workspaceId: string;
  saving: boolean;
  draftType: ComposerBlockType;
  setDraftType: (v: ComposerBlockType) => void;
  draftTitle: string;
  setDraftTitle: (v: string) => void;
  draftBody: string;
  setDraftBody: (v: string) => void;
  draftSignatureName: string;
  setDraftSignatureName: (v: string) => void;
  draftImageUrl: string | null;
  draftImagePath: string | null;
  draftImageAlt: string;
  draftImagePosition: ComposerBlockImagePosition;
  draftImageSize: ComposerBlockImageSize;
  draftImageAlign: ComposerBlockImageAlign;
  altError: boolean;
  setAltError: (v: boolean) => void;
  setDraftImageUrl: (v: string | null) => void;
  setDraftImagePath: (v: string | null) => void;
  setDraftImageAlt: (v: string) => void;
  setDraftImagePosition: (v: ComposerBlockImagePosition) => void;
  setDraftImageSize: (v: ComposerBlockImageSize) => void;
  setDraftImageAlign: (v: ComposerBlockImageAlign) => void;
  onSave: () => void;
};

export function BlockEditorDialog(p: Props) {
  return (
    <Dialog open={p.open} onOpenChange={p.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{p.editing ? "Edit block" : "New block"}</DialogTitle>
          <DialogDescription>
            Reusable text you can drag into any draft.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="block-type">Type</Label>
            <Select value={p.draftType} onValueChange={(v) => p.setDraftType(v as ComposerBlockType)}>
              <SelectTrigger id="block-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BLOCK_TYPE_ORDER.map((t) => (
                  <SelectItem key={t} value={t}>
                    {BLOCK_TYPE_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="block-title">Title</Label>
            <Input
              id="block-title"
              value={p.draftTitle}
              onChange={(e) => p.setDraftTitle(e.target.value)}
              placeholder="Short, descriptive label"
              autoFocus
            />
          </div>
          {p.draftType === "signature" && (
            <div className="space-y-1.5">
              <Label htmlFor="block-signature-name">Signature Name</Label>
              <Input
                id="block-signature-name"
                value={p.draftSignatureName}
                onChange={(e) => p.setDraftSignatureName(e.target.value)}
                placeholder="e.g. Jane Doe"
              />
              <p className="text-[11px] text-muted-foreground">
                The printed name. Also seeds the "Type signature with my font" dialog so the rasterized PNG only contains this name.
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="block-body">
              {p.draftType === "signature" ? "Bio / byline (optional)" : "Body"}
            </Label>
            <BlockBodyEditor
              value={p.draftBody}
              onChange={p.setDraftBody}
              placeholder={
                p.draftType === "signature"
                  ? "Bio, byline, or link that appears beneath your signature."
                  : "The text inserted when this block is dragged into a draft."
              }
            />
          </div>
          {(p.draftType === "signature" || p.draftType === "author_bio" || p.draftType === "product_cta") && (
            p.editing ? (
              <BlockImageField
                workspaceId={p.workspaceId}
                blockId={p.editing.id}
                imageUrl={p.draftImageUrl}
                imagePath={p.draftImagePath}
                imageAlt={p.draftImageAlt}
                imagePosition={p.draftImagePosition}
                imageSize={p.draftImageSize}
                imageAlign={p.draftImageAlign}
                blockTitle={p.draftTitle}
                signatureName={p.draftType === "signature" ? p.draftSignatureName : undefined}
                altError={p.altError}
                onChange={(next) => {
                  p.setDraftImageUrl(next.imageUrl);
                  p.setDraftImagePath(next.imagePath);
                  p.setDraftImageAlt(next.imageAlt);
                  p.setDraftImagePosition(next.imagePosition);
                  p.setDraftImageSize(next.imageSize);
                  p.setDraftImageAlign(next.imageAlign);
                  if (next.imageAlt.trim()) p.setAltError(false);
                }}
              />
            ) : (
              <p className="rounded-md border border-dashed border-border/60 bg-muted/20 p-3 text-xs text-muted-foreground">
                Save this block first, then re-open it to upload an image.
              </p>
            )
          )}

        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => p.onOpenChange(false)} disabled={p.saving}>
            Cancel
          </Button>
          <Button onClick={p.onSave} disabled={p.saving}>
            {p.saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            {p.editing ? "Save changes" : "Create block"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
